import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import { useToastHandler } from "../../../common/ToastHandler";
import { useConfig } from "../../../../context/ConfigContext";
import PrintableInvoice from "../Dashboard/Invoice/templates/PrintableInvoice";
import A4InvoiceTemplate from "../Dashboard/Invoice/templates/A4InvoiceTemplate";
import { getInvoiceLayoutSettings, INVOICE_TYPES, A4_DESIGN_OPTIONS, INVOICE_COLUMN_FIELDS, getDefaultInvoiceColumns } from "../Dashboard/Invoice/templates/invoiceLayoutDefaults";
import { normalizeInvoiceForPrint } from "../Dashboard/Invoice/templates/normalizeInvoiceForPrint";
import "./InvoiceLayoutSettings.css";

const INVOICE_TYPE_LABELS = {
  pos: "Quick Invoice",
  retail: "Retail Invoice",
  wholesale: "Wholesale Invoice",
  ecom: "E-Commerce",
};

const SAMPLE_INVOICE = {
  sales_invoice: {
    invoice_no: "INV-0001",
    date: new Date().toLocaleDateString(),
    customer_name: "John Doe",
    customer_address: "123 Main St, Springfield",
    customer_phone: "+1 555-0100",
    payable_amount: 128.5,
    round_off: 0,
    pay_method: "Cash",
    tax_amount: 8.5,
    discount_amount: 5,
  },
  sales_invoice_products: [
    { product_name: "Blue T-Shirt", sold_qty_stock: 2, selling_price: 20, discount: 2, tax_amount: 2.8, total_amount: 40 },
    { product_name: "Denim Jeans", sold_qty_stock: 1, selling_price: 45, discount: 3, tax_amount: 3.6, total_amount: 45 },
    { product_name: "Canvas Sneakers", sold_qty_stock: 1, selling_price: 43.5, discount: 0, tax_amount: 2.1, total_amount: 43.5 },
  ],
};

const CHECKBOX_FIELDS = [
  ["show_logo", "Show store logo"],
  ["show_store_address", "Show store address / contact"],
  ["show_customer_block", "Show customer name / address"],
  ["show_tax_breakdown", "Show tax / GST breakdown"],
  ["show_item_discount", "Show per-item discount column"],
  ["show_notes_footer", "Show notes / terms footer"],
  ["show_barcode", "Show barcode / QR (thermal only)"],
  ["show_signature_line", "Show signature line (A4 only)"],
];

const FONT_FAMILY_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "serif", label: "Serif (Georgia)" },
  { value: "modern", label: "Modern (Segoe UI)" },
  { value: "monospace", label: "Monospace (Courier)" },
];

// Advanced Template Editor: clicking a field directly on the live preview
// opens a small inline editor for it. Each entry describes what to show and
// which layout keys it edits; an optional "toggle" key is a companion
// show/hide switch bundled into the same popover.
const FIELD_EDIT_CONFIG = {
  business_name: {
    title: "Business Name",
    fields: [{ key: "business_name", label: "Business name", type: "text" }],
  },
  business_address: {
    title: "Business Address & Contact",
    toggle: { key: "show_store_address", label: "Show this section on the invoice" },
    fields: [
      { key: "business_address", label: "Address", type: "text" },
      { key: "business_contact", label: "Phone / email", type: "text" },
    ],
  },
  header_note: {
    title: "Header Note",
    fields: [{ key: "header_note", label: "Note shown under the header", type: "text" }],
  },
  footer_note: {
    title: "Footer Note",
    toggle: { key: "show_notes_footer", label: "Show footer note on the invoice" },
    fields: [{ key: "footer_note", label: "Footer note / terms", type: "textarea" }],
  },
  terms_text: {
    title: "Terms & Conditions",
    toggle: { key: "show_terms", label: "Show terms & conditions on the invoice" },
    fields: [{ key: "terms_text", label: "Terms & conditions text", type: "textarea" }],
  },
  bank_details: {
    title: "Bank Details",
    toggle: { key: "show_bank_details", label: "Show bank details on the invoice" },
    fields: [
      { key: "bank_name", label: "Bank name", type: "text" },
      { key: "bank_account_number", label: "Account number", type: "text" },
      { key: "bank_ifsc", label: "IFSC code", type: "text" },
      { key: "bank_branch", label: "Branch", type: "text" },
    ],
  },
};

// Images are stored inline as data URIs on the layout JSON (there's no asset
// upload endpoint for this yet), so keep them small -- warn past ~800KB.
const MAX_IMAGE_BYTES = 800 * 1024;
function readFileAsDataUrl(file, onResult, onError) {
  if (file.size > MAX_IMAGE_BYTES) {
    onError?.("Image is larger than 800KB -- please use a smaller file.");
    return;
  }
  const reader = new FileReader();
  reader.onload = () => onResult(reader.result);
  reader.onerror = () => onError?.("Could not read that image file.");
  reader.readAsDataURL(file);
}

const InvoiceLayoutSettings = () => {
  const { user } = useContext(AuthContext);
  const storeId = user?.store;
  const { Get, Patch, Post } = useApi();
  const { handleToast } = useToastHandler();
  const { invoiceLayoutSettings, setInvoiceLayoutSettings, storeInfo } = useConfig();

  const [configurationId, setConfigurationId] = useState(null);
  const [allLayouts, setAllLayouts] = useState(getInvoiceLayoutSettings(invoiceLayoutSettings));
  const [activeType, setActiveType] = useState("pos");
  const [saving, setSaving] = useState(false);
  const previewCanvasRef = useRef(null);
  const previewPageRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingValues, setEditingValues] = useState({});

  useEffect(() => {
    const fetchConfig = async () => {
      if (!storeId) return;
      try {
        const response = await Get("configurationForStore", { queryParams: { store: storeId } });
        const config = response?.data?.[0];
        if (config) {
          setConfigurationId(config.id);
          const merged = getInvoiceLayoutSettings(config.invoice_layout_settings);
          setAllLayouts(merged);
          if (setInvoiceLayoutSettings) setInvoiceLayoutSettings(config.invoice_layout_settings || {});
        }
      } catch (error) {
        console.error("Failed to load invoice layout settings:", error);
      }
    };
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const draftLayout = allLayouts[activeType];

  // A4 pages are a fixed physical width (~794px @ 96dpi); the preview column
  // is often narrower than that, so scale the rendered page down to fit
  // instead of letting it overflow/clip. Thermal receipts are already narrow
  // and render at natural size.
  useLayoutEffect(() => {
    if (draftLayout?.template !== "a4") {
      setPreviewScale(1);
      return;
    }
    const recalc = () => {
      const containerWidth = previewCanvasRef.current?.clientWidth || 0;
      const pageWidth = previewPageRef.current?.scrollWidth || 794;
      if (!containerWidth || !pageWidth) return;
      setPreviewScale(Math.min(1, (containerWidth - 24) / pageWidth));
    };
    recalc();
    const observer = new ResizeObserver(recalc);
    if (previewCanvasRef.current) observer.observe(previewCanvasRef.current);
    window.addEventListener("resize", recalc);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", recalc);
    };
  }, [draftLayout?.template, activeType]);

  const updateDraft = (field, value) => {
    setAllLayouts((prev) => ({
      ...prev,
      [activeType]: { ...prev[activeType], [field]: value },
    }));
  };

  // Called when a clickable region on the live preview is clicked (advanced
  // Template Editor mode). Pure toggle fields flip immediately; everything
  // else opens the matching inline popover pre-filled with current values.
  const handleEditField = (fieldKey) => {
    if (fieldKey === "show_amount_in_words") {
      updateDraft("show_amount_in_words", !draftLayout.show_amount_in_words);
      return;
    }
    const config = FIELD_EDIT_CONFIG[fieldKey];
    if (!config) return;
    const values = {};
    config.fields.forEach((f) => {
      values[f.key] = draftLayout[f.key] || "";
    });
    if (config.toggle) values[config.toggle.key] = draftLayout[config.toggle.key] !== false;
    setEditingValues(values);
    setEditingField(fieldKey);
  };

  const saveEditingField = () => {
    setAllLayouts((prev) => ({
      ...prev,
      [activeType]: { ...prev[activeType], ...editingValues },
    }));
    setEditingField(null);
  };

  const updateColumn = (colKey, field, value) => {
    setAllLayouts((prev) => {
      const current = prev[activeType];
      const columns = { ...getDefaultInvoiceColumns(), ...(current.columns || {}) };
      return {
        ...prev,
        [activeType]: {
          ...current,
          columns: { ...columns, [colKey]: { ...columns[colKey], [field]: value } },
        },
      };
    });
  };

  const [imageError, setImageError] = useState("");

  const handleImageUpload = (field, file) => {
    if (!file) return;
    setImageError("");
    readFileAsDataUrl(
      file,
      (dataUrl) => updateDraft(field, dataUrl),
      (message) => setImageError(message)
    );
  };

  const handleSave = async () => {
    if (!storeId) {
      handleToast({ type: "ERROR", message: "Store ID not found!" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        store: storeId,
        invoice_layout_settings: { [activeType]: draftLayout },
      };
      if (configurationId) {
        await Patch("configurationForStore", configurationId, payload);
      } else {
        const response = await Post("configurationForStore", payload);
        setConfigurationId(response?.data?.id || null);
      }
      if (setInvoiceLayoutSettings) {
        setInvoiceLayoutSettings({ ...(invoiceLayoutSettings || {}), [activeType]: draftLayout });
      }
      handleToast({ type: "SUCCESS", message: `${INVOICE_TYPE_LABELS[activeType]} layout saved successfully!` });
    } catch (error) {
      console.error("Failed to save invoice layout:", error);
      handleToast({ type: "ERROR", message: "Failed to save invoice layout settings." });
    } finally {
      setSaving(false);
    }
  };

  if (!draftLayout) return null;

  return (
    <div className="invoice-layout-settings">
      <div className="invoice-layout-toolbar">
        <div className="invoice-layout-tabs">
          {INVOICE_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              className={`invoice-layout-tab ${activeType === type ? "active" : ""}`}
              onClick={() => setActiveType(type)}
            >
              {INVOICE_TYPE_LABELS[type]}
            </button>
          ))}
        </div>

        <div className="invoice-layout-toolbar-controls">
          <div className="invoice-layout-toolbar-group invoice-layout-radio-row">
            <label>
              <input
                type="radio"
                name="template"
                checked={draftLayout.template === "a4"}
                onChange={() => updateDraft("template", "a4")}
              />
              A4
            </label>
            <label>
              <input
                type="radio"
                name="template"
                checked={draftLayout.template === "thermal"}
                onChange={() => updateDraft("template", "thermal")}
              />
              Thermal
            </label>
          </div>

          {draftLayout.template === "thermal" && (
            <div className="invoice-layout-toolbar-group">
              <select value={draftLayout.paper_width} onChange={(e) => updateDraft("paper_width", e.target.value)}>
                <option value="58mm">58mm</option>
                <option value="80mm">80mm</option>
              </select>
            </div>
          )}

          <div className="invoice-layout-toolbar-group">
            <select value={draftLayout.font_size} onChange={(e) => updateDraft("font_size", e.target.value)}>
              <option value="small">Small font</option>
              <option value="medium">Medium font</option>
              <option value="large">Large font</option>
            </select>
          </div>

          <div className="invoice-layout-toolbar-group">
            <select value={draftLayout.font_family} onChange={(e) => updateDraft("font_family", e.target.value)}>
              {FONT_FAMILY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="invoice-layout-toolbar-group invoice-layout-color-group">
            <label htmlFor="invoice-brand-color">Brand color</label>
            <input
              id="invoice-brand-color"
              type="color"
              value={draftLayout.brand_color || "#0056B3"}
              onChange={(e) => updateDraft("brand_color", e.target.value)}
            />
          </div>

          {draftLayout.template === "a4" && (
            <button
              type="button"
              className={`invoice-layout-editor-btn ${editorOpen ? "active" : ""}`}
              onClick={() => setEditorOpen((v) => !v)}
            >
              {editorOpen ? "Close Editor" : "Template Editor"}
            </button>
          )}

          <button type="button" className="invoice-layout-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {draftLayout.template === "a4" && (
        <div className="invoice-layout-gallery">
          <div className="invoice-layout-gallery-label">Choose a design</div>
          <div className="invoice-layout-gallery-row">
            {A4_DESIGN_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`invoice-layout-gallery-card ${(draftLayout.design || "classic") === opt.key ? "active" : ""}`}
                onClick={() => updateDraft("design", opt.key)}
              >
                <div className="invoice-layout-gallery-thumb">
                  <div className="invoice-layout-gallery-thumb-inner">
                    <A4InvoiceTemplate
                      layout={{ ...draftLayout, design: opt.key }}
                      invoice={normalizeInvoiceForPrint(SAMPLE_INVOICE, activeType)}
                      storeInfo={storeInfo || {}}
                    />
                  </div>
                </div>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="invoice-layout-main-row">
      <div className="invoice-layout-body">
        <div className="invoice-layout-form">
          <h3>{INVOICE_TYPE_LABELS[activeType]} Content</h3>

          <div className="invoice-layout-field-group">
            <label>Business Name (shown on invoice)</label>
            <input
              type="text"
              value={draftLayout.business_name}
              onChange={(e) => updateDraft("business_name", e.target.value)}
              placeholder={storeInfo?.name || "Your store name"}
            />
          </div>

          <div className="invoice-layout-field-group">
            <label>Business Address (shown on invoice)</label>
            <input
              type="text"
              value={draftLayout.business_address}
              onChange={(e) => updateDraft("business_address", e.target.value)}
              placeholder={storeInfo?.address || "Street, city, state"}
            />
          </div>

          <div className="invoice-layout-field-group">
            <label>Business Contact (shown on invoice)</label>
            <input
              type="text"
              value={draftLayout.business_contact}
              onChange={(e) => updateDraft("business_contact", e.target.value)}
              placeholder={storeInfo?.contact || "Phone / email"}
            />
          </div>

          <div className="invoice-layout-field-group invoice-layout-image-row">
            <div className="invoice-layout-image-field">
              <label>Header Image</label>
              {draftLayout.header_image && (
                <div className="invoice-layout-image-preview">
                  <img src={draftLayout.header_image} alt="Header preview" />
                  <button type="button" onClick={() => updateDraft("header_image", "")}>Remove</button>
                </div>
              )}
              <input type="file" accept="image/*" onChange={(e) => handleImageUpload("header_image", e.target.files?.[0])} />
            </div>
            <div className="invoice-layout-image-field">
              <label>Footer Image</label>
              {draftLayout.footer_image && (
                <div className="invoice-layout-image-preview">
                  <img src={draftLayout.footer_image} alt="Footer preview" />
                  <button type="button" onClick={() => updateDraft("footer_image", "")}>Remove</button>
                </div>
              )}
              <input type="file" accept="image/*" onChange={(e) => handleImageUpload("footer_image", e.target.files?.[0])} />
            </div>
          </div>
          {imageError && <div className="invoice-layout-image-error">{imageError}</div>}

          <div className="invoice-layout-field-group invoice-layout-checkbox-grid">
            {CHECKBOX_FIELDS.map(([key, label]) => (
              <label key={key} className="invoice-layout-checkbox">
                <input
                  type="checkbox"
                  checked={!!draftLayout[key]}
                  onChange={(e) => updateDraft(key, e.target.checked)}
                />
                {label}
              </label>
            ))}
          </div>

          <div className="invoice-layout-field-group">
            <label>Header Note</label>
            <input
              type="text"
              value={draftLayout.header_note}
              onChange={(e) => updateDraft("header_note", e.target.value)}
              placeholder="e.g. Welcome to our store"
            />
          </div>

          <div className="invoice-layout-field-group">
            <label>Footer Note / Terms</label>
            <input
              type="text"
              value={draftLayout.footer_note}
              onChange={(e) => updateDraft("footer_note", e.target.value)}
              placeholder="e.g. Thank you for shopping with us"
            />
          </div>
        </div>

        <div className="invoice-layout-preview">
          <div className="invoice-layout-preview-label">Live Preview</div>
          <div
            ref={previewCanvasRef}
            className={`invoice-layout-preview-canvas ${draftLayout.template === "thermal" ? "is-thermal" : "is-a4"}`}
          >
            <div
              ref={previewPageRef}
              className="invoice-layout-preview-page"
              style={draftLayout.template === "a4" ? { transform: `scale(${previewScale})`, transformOrigin: "top center" } : undefined}
            >
              <PrintableInvoice
                layout={draftLayout}
                invoice={normalizeInvoiceForPrint(SAMPLE_INVOICE, activeType)}
                storeInfo={storeInfo || {}}
                editing={editorOpen && draftLayout.template === "a4"}
                onEditField={handleEditField}
              />
            </div>
          </div>
        </div>
      </div>

      {editorOpen && draftLayout.template === "a4" && editingField && FIELD_EDIT_CONFIG[editingField] && (
        <div className="invoice-layout-field-popover-backdrop" onClick={() => setEditingField(null)}>
          <div className="invoice-layout-field-popover" onClick={(e) => e.stopPropagation()}>
            <h4>{FIELD_EDIT_CONFIG[editingField].title}</h4>

            {FIELD_EDIT_CONFIG[editingField].toggle && (
              <label className="invoice-layout-checkbox invoice-layout-popover-toggle">
                <input
                  type="checkbox"
                  checked={editingValues[FIELD_EDIT_CONFIG[editingField].toggle.key] !== false}
                  onChange={(e) =>
                    setEditingValues((prev) => ({
                      ...prev,
                      [FIELD_EDIT_CONFIG[editingField].toggle.key]: e.target.checked,
                    }))
                  }
                />
                {FIELD_EDIT_CONFIG[editingField].toggle.label}
              </label>
            )}

            {FIELD_EDIT_CONFIG[editingField].fields.map((f) =>
              f.type === "textarea" ? (
                <div className="invoice-layout-field-group" key={f.key}>
                  <label>{f.label}</label>
                  <textarea
                    rows={4}
                    value={editingValues[f.key] || ""}
                    onChange={(e) => setEditingValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  />
                </div>
              ) : (
                <div className="invoice-layout-field-group" key={f.key}>
                  <label>{f.label}</label>
                  <input
                    type="text"
                    value={editingValues[f.key] || ""}
                    onChange={(e) => setEditingValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  />
                </div>
              )
            )}

            <div className="invoice-layout-popover-actions">
              <button type="button" className="invoice-layout-popover-cancel" onClick={() => setEditingField(null)}>
                Cancel
              </button>
              <button type="button" className="invoice-layout-save-btn" onClick={saveEditingField}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {editorOpen && draftLayout.template === "a4" && (
        <div className="invoice-layout-column-editor">
          <div className="invoice-layout-column-editor-header">
            <h3>Adjust Document Column</h3>
            <button type="button" onClick={() => setEditorOpen(false)} aria-label="Close">&times;</button>
          </div>
          <p className="invoice-layout-column-editor-sub">Adjust document width and column visibility</p>

          <div className="invoice-layout-column-editor-rows">
            <div className="invoice-layout-column-row invoice-layout-column-row-head">
              <span>Column Name</span>
              <span>Width</span>
              <span>Visible</span>
            </div>
            {INVOICE_COLUMN_FIELDS.map((field) => {
              const col = (draftLayout.columns && draftLayout.columns[field.key]) || { visible: true, width: 0 };
              return (
                <div className="invoice-layout-column-row" key={field.key}>
                  <span>{field.label}</span>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={col.width || 0}
                    onChange={(e) => updateColumn(field.key, "width", Number(e.target.value))}
                  />
                  <label className="invoice-layout-toggle">
                    <input
                      type="checkbox"
                      checked={col.visible !== false}
                      onChange={(e) => updateColumn(field.key, "visible", e.target.checked)}
                    />
                    <span className="invoice-layout-toggle-slider" />
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default InvoiceLayoutSettings;
